import type { ApiISODateString } from '#/api/types/ApiTypedDate';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { getMonthName } from '#/utils/string/date/dateI18n';

/**
 * @example
 *
 * Given the date 2026-04-07
 *
 * //=> EN (d/M/yyyy)
 * 4/7/2026
 *
 * //=> ES, PT (M/d/yyyy)
 * 7/4/2026
 */
export function shorterIsoDateString(
  date: ApiISODateString,
  { locale }: { locale: I18nLocale },
) {
  const [year, month, day] = date.split('-').map(Number);

  switch (locale) {
    case I18nLocale.EN:
      return `${month}/${day}/${year}`;
    case I18nLocale.ES:
    case I18nLocale.PT:
      return `${day}/${month}/${year}`;
  }
}

/**
 * Formats as 'MMM d, yyyy'
 *
 * @example
 *
 * Given the date 2026-04-07
 *
 * //=> EN
 * April 7, 2026
 *
 * //=> ES
 * April 7, 2026
 *
 * //=> PT
 * April 7, 2026
 */
export function humanIsoDateString(
  date: ApiISODateString,
  {
    locale,
    monthFormat,
  }: { locale: I18nLocale; monthFormat: 'short' | 'long' },
) {
  const [year, month, day] = date.split('-').map(Number);
  const monthName = getMonthName(month, { locale, format: monthFormat });

  return `${monthName} ${day}, ${year}`;
}

export function humanMonthAndYearIsoDateString(
  date: ApiISODateString,
  {
    locale,
    monthFormat,
  }: { locale: I18nLocale; monthFormat: 'short' | 'long' },
) {
  const [year, month] = date.split('-').map(Number);
  const monthName = getMonthName(month, { locale, format: monthFormat });

  return `${monthName} ${year}`;
}
